import { useState, useEffect } from 'react';

/**
 * A custom hook to persist form state to localStorage.
 * 
 * @param {string} formKey - The unique key to use for localStorage.
 * @param {any} initialData - The initial state for the form.
 * @param {boolean} [shouldPersist=true] - Optional flag to disable persistence (e.g., when editing).
 * @returns {[any, Function, Function]} - Returns [formData, setFormData, clearDraft]
 */
const useFormDraft = (formKey, initialData, shouldPersist = true) => {
  const [formData, setFormData] = useState(() => {
    if (!shouldPersist) return initialData;
    
    try {
      const savedDraft = localStorage.getItem(formKey);
      if (savedDraft) {
        return JSON.parse(savedDraft);
      }
    } catch (e) {
      console.error("Failed to parse draft data", e);
    }
    return initialData;
  });

  useEffect(() => {
    if (!shouldPersist) return;

    try {
      localStorage.setItem(formKey, JSON.stringify(formData));
    } catch (e) {
      console.error("Failed to save draft data", e);
    }
  }, [formKey, formData, shouldPersist]);

  const clearDraft = () => {
    try {
      localStorage.removeItem(formKey);
    } catch (e) {
      console.error("Failed to remove draft data", e);
    }
    setFormData(initialData);
  };

  return [formData, setFormData, clearDraft];
};

export default useFormDraft;
